<script>
  import { onMount } from "svelte";
  import { DataTable } from "carbon-components-svelte";
  import {
    Pagination,
    BadgeColor,
    InfoBlock,
    SectionWrapper,
    Select,
    TextInput,
    Button,
  } from "paxray-components-svelte";
  import { EDIT_ICON, TRASH_ICON, UPLOAD_ICON } from "@utils/icons.js";
  import Modal from "lib/components/Modal.svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import GroupCell from "../../settings/components/GroupCell.svelte";
  import SearchInput from "../../settings/components/SearchInput.svelte";
  import ImportTeamUsersModal from "../../damprojects-frontend/components/ImportTeamUsersModal.svelte";

  const headers = [
    { key: "status", value: "Status" },
    { key: "first_name", value: "First Name" },
    { key: "last_name", value: "Last Name" },
    { key: "name", value: "System Login Name" },
    { key: "email", value: "E-Mail" },
    { key: "domain", value: "Domain" },
    { key: "team", value: "Assigned Team" },
    { key: "project", value: "Project" },
    { key: "lastupdate", value: "Last Contact" },
    { key: "actions", value: "Actions" },
  ];
  const statusOptions = [
    { value: "Online", text: "Online" },
    { value: "Offline", text: "Offline" },
  ];
  const completenessOptions = [
    { value: "missing-any", text: "Any information missing" },
    { value: "missing-first-name", text: "First name missing" },
    { value: "missing-last-name", text: "Last name missing" },
    { value: "missing-email", text: "E-mail address missing" },
    { value: "complete", text: "All information complete" },
  ];

  let clients = [];
  let loading = true;
  let error = "";
  let searchClient = "";
  let selectedDomain = "";
  let selectedStatus = "";
  let selectedCompleteness = "";
  let page = 1;
  let pageSize = 10;
  let importOpen = false;
  let importing = false;
  let importMessage = "";
  let importMessageType = "hint";
  let deleteOpen = false;
  let deleteClient = null;
  let deleting = false;
  let deleteError = "";
  let editOpen = false;
  let editClient = null;
  let editForm = { first_name: "", last_name: "", email: "" };
  let saving = false;
  let editError = "";
  $: invalidEmail = Boolean(
    editForm.email.trim() &&
      !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(editForm.email.trim()),
  );

  function openEditor(id) {
    editClient = clients.find((client) => client.id === id);
    if (!editClient) return;
    editForm = {
      first_name: editClient.missingFirstName ? "" : editClient.first_name,
      last_name: editClient.missingLastName ? "" : editClient.last_name,
      email: editClient.missingEmail ? "" : editClient.email,
    };
    editError = "";
    editOpen = true;
  }

  async function readClientResponse(response) {
    if (
      !(response.headers.get("content-type") || "").includes("application/json")
    ) {
      throw new Error(
        "The client management service is unavailable. Please restart or update the DAM Projects backend and try again.",
      );
    }
    return response.json();
  }

  async function saveClient() {
    if (!editClient || saving || invalidEmail) return;
    const id = editClient.id;
    saving = true;
    editError = "";
    try {
      const response = await fetchWithSession(
        `/api/damprojects/admin/dam-users/${encodeURIComponent(id)}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(editForm),
          suppressSuccessToast: true,
          suppressErrorToast: true,
        },
      );
      const payload = await readClientResponse(response);
      if (!response.ok || !payload.ok)
        throw new Error(
          payload.message || "Client information could not be saved.",
        );
      const { first_name, last_name, email } = payload.data;
      clients = clients.map((client) =>
        client.id === id
          ? {
              ...client,
              first_name: first_name || "-",
              last_name: last_name || "-",
              email: email || "-",
              missingFirstName: !first_name,
              missingLastName: !last_name,
              missingEmail: !email,
            }
          : client,
      );
      editOpen = false;
      editClient = null;
    } catch (exception) {
      editError = exception.message || "Client information could not be saved.";
    } finally {
      saving = false;
    }
  }

  function confirmDelete(id) {
    deleteClient = clients.find((client) => client.id === id);
    deleteError = "";
    deleteOpen = Boolean(deleteClient);
  }

  async function removeClient() {
    if (!deleteClient || deleting) return;
    const id = deleteClient.id;
    deleting = true;
    deleteError = "";
    try {
      const response = await fetchWithSession(
        `/api/damprojects/admin/dam-users/${encodeURIComponent(id)}`,
        {
          method: "DELETE",
          suppressSuccessToast: true,
          suppressErrorToast: true,
        },
      );
      const payload = await readClientResponse(response);
      if (!response.ok || !payload.ok) {
        throw new Error(payload.message || "Client could not be deleted.");
      }
      clients = clients.filter((client) => client.id !== id);
      deleteOpen = false;
      deleteClient = null;
    } catch (exception) {
      deleteError = exception.message || "Client could not be deleted.";
    } finally {
      deleting = false;
    }
  }

  $: normalizedSearch = searchClient.trim().toLowerCase();
  $: domainOptions = [
    ...uniqueValues(
      clients.map((client) => client.domain).filter((domain) => domain !== "-"),
    )
      .sort((left, right) => left.localeCompare(right))
      .map((domain) => ({ value: domain, text: domain })),
    ...(clients.some((client) => client.domain === "-")
      ? [{ value: "__missing_domain__", text: "No domain" }]
      : []),
  ];
  $: filteredClients = clients.filter(
    (client) =>
      matchesSearch(client, normalizedSearch) &&
      matchesDomain(client, selectedDomain) &&
      (!selectedStatus || client.status === selectedStatus) &&
      matchesCompleteness(client, selectedCompleteness),
  );
  $: visibleClients = filteredClients.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );
  $: if (page > Math.max(Math.ceil(filteredClients.length / pageSize), 1)) {
    page = 1;
  }

  onMount(loadClients);

  function onSearch(event) {
    searchClient = event.detail || "";
    page = 1;
  }

  function resetPage() {
    page = 1;
  }

  function matchesSearch(client, search) {
    if (!search) return true;
    return [
      client.status,
      client.first_name,
      client.last_name,
      client.name,
      client.email,
      client.domain,
      ...client.team.map((item) => item.name),
      ...client.project.map((item) => item.name),
      client.lastupdate,
    ].some((value) =>
      String(value || "")
        .toLowerCase()
        .includes(search),
    );
  }

  function matchesDomain(client, domain) {
    if (!domain) return true;
    return domain === "__missing_domain__"
      ? client.domain === "-"
      : client.domain === domain;
  }

  function matchesCompleteness(client, completeness) {
    if (!completeness) return true;
    if (completeness === "missing-any") {
      return (
        client.missingFirstName || client.missingLastName || client.missingEmail
      );
    }
    if (completeness === "missing-first-name") return client.missingFirstName;
    if (completeness === "missing-last-name") return client.missingLastName;
    if (completeness === "missing-email") return client.missingEmail;
    if (completeness === "complete") {
      return (
        !client.missingFirstName &&
        !client.missingLastName &&
        !client.missingEmail
      );
    }
    return true;
  }

  async function fetchJson(url) {
    const response = await fetchWithSession(url);
    if (!response.ok)
      throw new Error("Client information could not be loaded.");
    return response.json();
  }

  async function loadClients() {
    loading = true;
    error = "";

    try {
      const [users, projectGroups] = await Promise.all([
        fetchJson("/api/damprojects/getDAMTeamUsers"),
        fetchJson("/api/damprojects/projects"),
      ]);
      const projects = uniqueProjects([
        ...(projectGroups.analysis || []),
        ...(projectGroups.running || []),
      ]);
      const teamGroups = await Promise.all(
        projects.map(async (project) => ({
          project,
          teams: await fetchJson(
            `/api/damprojects/dam-teams?idproject=${encodeURIComponent(project.id)}`,
          ),
        })),
      );
      const assignments = buildAssignments(teamGroups);
      const projectsById = new Map(
        projects.map((project) => [String(project.id), project.name]),
      );

      clients = users.map((user) =>
        buildClientRow(user, assignments, projectsById),
      );
    } catch (exception) {
      error = exception.message;
      clients = [];
    } finally {
      loading = false;
    }
  }

  async function importClients(event) {
    importing = true;
    importMessage = "";

    try {
      const response = await fetchWithSession(
        "/api/damprojects/admin/dam-users/import",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ rows: event.detail.rows || [] }),
          suppressSuccessToast: true,
        },
      );
      const contentType = response.headers.get("content-type") || "";
      if (!contentType.toLowerCase().includes("application/json")) {
        throw new Error(
          "The user information service is not available yet. Please restart or update the DAM Projects backend and try again.",
        );
      }

      let payload;
      try {
        payload = await response.json();
      } catch {
        throw new Error("The import service returned an invalid response.");
      }
      if (!response.ok || !payload.ok) {
        throw new Error(
          payload.message || "Client information could not be imported.",
        );
      }

      const result = payload.data;
      const issuePreview = (result.issues || [])
        .slice(0, 3)
        .map((issue) => `Row ${issue.row}: ${issue.message}`)
        .join(" ");
      const summary =
        [
          `updated ${result.updated}`,
          `unchanged ${result.unchanged}`,
          `skipped ${result.skipped}`,
        ].join(", ") + ".";
      importMessage = issuePreview ? `${summary} ${issuePreview}` : summary;
      importMessageType = result.skipped ? "warning" : "hint";
      importOpen = false;
      await loadClients();
    } catch (exception) {
      importMessage =
        exception.message || "Client information could not be imported.";
      importMessageType = "warning";
    } finally {
      importing = false;
    }
  }

  function uniqueProjects(projects) {
    return [
      ...new Map(
        projects
          .filter((project) => project.id)
          .map((project) => [project.id, project]),
      ).values(),
    ];
  }

  function buildAssignments(teamGroups) {
    const assignments = new Map();

    for (const { project, teams } of teamGroups) {
      for (const team of teams) {
        for (const user of team.damUsers || []) {
          const userId = String(user.id || user);
          const current = assignments.get(userId) || [];
          current.push({ team: team.name, project: project.name });
          assignments.set(userId, current);
        }
      }
    }

    return assignments;
  }

  function buildClientRow(user, assignments, projectsById) {
    const rawName = String(user.name || "");
    const nameParts = rawName.split("\\");
    const hasDomain = nameParts.length > 1;
    const userAssignments = assignments.get(String(user.id)) || [];
    const teamNames = uniqueValues(userAssignments.map((item) => item.team));
    const projectNames = uniqueValues(
      userAssignments.map((item) => item.project),
    );
    const fallbackProject = projectsById.get(String(user.idproject));
    const firstName = String(user.first_name || user.firstName || "").trim();
    const lastName = String(user.last_name || user.lastName || "").trim();
    const email = String(user.email || "").trim();

    return {
      id: user.id,
      status: getStatus(user.lastupdate),
      first_name: firstName || "-",
      last_name: lastName || "-",
      name: hasDomain
        ? nameParts.slice(1).join("\\").replace(/^\\+/, "")
        : rawName,
      email: email || "-",
      domain: hasDomain && nameParts[0] ? nameParts[0] : "-",
      missingFirstName: !firstName,
      missingLastName: !lastName,
      missingEmail: !email,
      team: teamNames.map((name) => ({ name })),
      project: (projectNames.length
        ? projectNames
        : [fallbackProject].filter(Boolean)
      ).map((name) => ({ name })),
      lastupdate: formatDate(user.lastupdate),
    };
  }

  function uniqueValues(values) {
    return [...new Set(values.filter(Boolean))];
  }

  function getStatus(value) {
    const lastContact = new Date(value).getTime();
    const age = Date.now() - lastContact;
    return Number.isFinite(lastContact) && age >= 0 && age <= 60 * 60 * 1000
      ? "Online"
      : "Offline";
  }

  function formatDate(value) {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    const pad = (part) => String(part).padStart(2, "0");
    const formattedDate = `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${String(date.getFullYear()).slice(-2)}`;
    const isOlderThanSevenDays =
      Date.now() - date.getTime() > 7 * 24 * 60 * 60 * 1000;

    return isOlderThanSevenDays
      ? formattedDate
      : `${formattedDate} • ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }
</script>

<SectionWrapper fullscreen shrinkContent embedded>
  <section slot="main">
    <div class="embedded-toolbar">
      <div class="filter-field">
        <Select
          labelText="Domains"
          items={domainOptions}
          selected={selectedDomain}
          fitContent
          size="xl"
          on:update={(event) => {
            selectedDomain = event.detail;
            resetPage();
          }}
          color="dark"
          placeholder="All domains"
        />
      </div>
      <div class="filter-field">
        <Select
          labelText="Information"
          items={completenessOptions}
          selected={selectedCompleteness}
          fitContent
          size="xl"
          on:update={(event) => {
            selectedCompleteness = event.detail;
            resetPage();
          }}
          color="dark"
          placeholder="All client data"
        />
      </div>
      <div class="filter-field">
        <Select
          labelText="Status"
          items={statusOptions}
          selected={selectedStatus}
          fitContent
          size="xl"
          on:update={(event) => {
            selectedStatus = event.detail;
            resetPage();
          }}
          color="dark"
          placeholder="All statuses"
        />
      </div>
      <div class="filter-field search-field">
        <span class="filter-label">Search</span>
        <SearchInput placeholder="Search clients..." on:search={onSearch} />
      </div>
      <Button
        text="Update User Information (xlsx)"
        kind="secondary"
        iconLeft
        icon={UPLOAD_ICON}
        disabled={importing}
        on:click={() => (importOpen = true)}
      />
    </div>

    {#if importMessage}
      {#key importMessage}
        <InfoBlock
          type={importMessageType}
          heading={importMessageType === "hint"
            ? "Import completed"
            : "Import result"}
          message={importMessage}
        />
      {/key}
    {/if}

    {#if loading}
      <p>Loading clients...</p>
    {:else if error}
      <p class="error">{error}</p>
    {:else if !clients.length}
      <p>No clients have registered yet.</p>
    {:else if !filteredClients.length}
      <p>No clients match the selected filters.</p>
    {:else}
      <DataTable {headers} rows={visibleClients} class="custom-table">
        <svelte:fragment slot="cell" let:row let:cell>
          {#if cell.key === "actions"}
            <div class="row-actions">
              <Button
                kind="secondary"
                size="small"
                icon={EDIT_ICON}
                iconDescription="Edit client"
                aria-label="Edit client"
                disabled={deleting || importing || saving}
                on:click={() => openEditor(row.id)}
              />
              <Button
                kind="secondary"
                size="small"
                icon={TRASH_ICON}
                iconDescription="Delete client"
                aria-label="Delete client"
                disabled={deleting || importing || saving}
                on:click={() => confirmDelete(row.id)}
              />
            </div>
          {:else if cell.key === "team" || cell.key === "project"}
            <GroupCell {cell} />
          {:else if cell.key === "status"}
            <span
              class="client-status-pill"
              class:online={cell.value === "Online"}
              class:offline={cell.value === "Offline"}
            >
              <BadgeColor text={cell.value} color="client-status" small />
            </span>
          {:else if cell.key === "lastupdate"}
            {#if cell.value?.includes(" • ")}
              {cell.value.split(" • ")[0]}
              <strong class="last-contact-separator">•</strong>
              {cell.value.split(" • ")[1]}
            {:else}
              {cell.value || "-"}
            {/if}
          {:else}
            {cell?.value || "-"}
          {/if}
        </svelte:fragment>
      </DataTable>
      <Pagination
        dark
        bind:page
        bind:pageSize
        totalItems={filteredClients.length}
        pageSizes={[10, 25, 50]}
      />
    {/if}
  </section>
</SectionWrapper>

<Modal
  bind:open={editOpen}
  modalHeading="Edit client"
  modalClass="client-management-modal"
  autowidth
  subtitle={editClient?.name || ""}
  actions
  primaryButtonText={saving ? "Saving..." : "Save"}
  primaryButtonVariant="secondary"
  primaryButtonValidations={saving || invalidEmail}
  primaryButtonAction={saveClient}
>
  <div class="client-editor">
    <TextInput
      id="client-first-name"
      labelText="First Name"
      bind:value={editForm.first_name}
      maxlength={300}
      disabled={saving}
    />
    <TextInput
      id="client-last-name"
      labelText="Last Name"
      bind:value={editForm.last_name}
      maxlength={300}
      disabled={saving}
    />
    <TextInput
      id="client-email"
      labelText="E-Mail"
      type="email"
      bind:value={editForm.email}
      maxlength={300}
      disabled={saving}
      invalid={invalidEmail}
      invalidText="Enter a valid e-mail address."
    />
    {#if editError}<p class="error" role="alert">{editError}</p>{/if}
  </div>
</Modal>

<Modal
  bind:open={deleteOpen}
  modalHeading="Delete client?"
  modalClass="client-management-modal"
  autowidth
  actions
  primaryButtonText={deleting ? "Deleting..." : "Delete client"}
  primaryButtonVariant="secondary"
  primaryButtonValidations={deleting}
  primaryButtonAction={removeClient}
>
  <p>
    Delete “{deleteClient?.domain !== "-"
      ? `${deleteClient?.domain}\\`
      : ""}{deleteClient?.name || ""}” and its saved user information,
    team/project assignments and permissions?
  </p>
  <p>
    If the sensor requests its configuration again, the client will reappear
    without these details and assignments.
  </p>
  {#if deleteError}<p class="error" role="alert">{deleteError}</p>{/if}
</Modal>

<ImportTeamUsersModal
  bind:open={importOpen}
  loading={importing}
  includeTeam={false}
  existingUsersOnly
  heading="Update User Information"
  projectName="Client_Management"
  on:submit={importClients}
  on:close={() => (importOpen = false)}
/>

<style>
  :global(.client-management-modal .bx--modal-container) {
    width: min(36rem, calc(100vw - 2rem)) !important;
    padding: 1.5rem !important;
  }
  :global(.client-management-modal .bx--modal-header) {
    margin: 0 0 1rem;
    padding: 0 2rem 0 0;
  }
  :global(.client-management-modal .bx--modal-content) {
    width: 100%;
    padding: 0 !important;
  }
  :global(.client-management-modal .modal_wrapper) {
    padding-bottom: 0 !important;
  }
  :global(.client-management-modal .divider-margin) {
    margin: 1.5rem 0 0;
  }
  :global(.client-management-modal .buttons-container) {
    margin-top: 1.5rem;
  }
  .row-actions {
    display: flex;
    gap: 0.5rem;
  }

  .client-editor {
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
  }
  section {
    width: 100%;
  }

  .embedded-toolbar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    width: 100%;
    gap: var(--control-gap);
    margin-bottom: var(--control-gap) !important;
  }

  .filter-field {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 0.25rem;
  }

  .filter-label {
    color: var(--pax-black);
    font-size: 0.75rem;
    font-weight: 400;
  }

  .search-field {
    flex: 1;
    min-width: 12rem;
  }

  .search-field :global(.search-input) {
    border-color: var(--pax-black);
  }

  .embedded-toolbar :global(button) {
    align-self: flex-end;
  }

  .error {
    color: var(--pax-red);
  }

  .client-status-pill.online {
    --custom-color: var(--pax-green);
  }

  .client-status-pill.offline {
    --custom-color: var(--pax-red);
  }

  .last-contact-separator {
    color: var(--pax-black);
    font-weight: 700;
  }
</style>
